import React, { useEffect, useState } from 'react';

import numberFormatter from '../../../common/numberFormatter';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import {
  TopCompanyInvoicesDto,
  ResponseError,
} from '../../../common/spdCore/autogenerated/spdApiClient/index';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';
import ValidationAlert from '../../../app/components/ValidationAlert';

import {
  DateRangeState,
  TopCompaniesReportShell,
  toDateEnd,
  toDateStart,
} from './SuperAdminTopCompaniesBase';
import { CompanyRankingList } from './SuperAdminCompanyRankingList';

export const SuperAdminTopCompaniesInvoicesTab: React.FC = () => {
  const spd = useSpdCore();

  const [filters, setFilters] = useState<DateRangeState>({
    dateFrom: '',
    dateTo: '',
  });
  const [appliedFilters, setAppliedFilters] = useState<DateRangeState>({
    dateFrom: '',
    dateTo: '',
  });
  const [invoices, setInvoices] = useState<TopCompanyInvoicesDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState<string[]>([]);

  const loadData = async (nextFilters: DateRangeState) => {
    setLoading(true);
    try {
      const result = await spd.reports.reportTopCompaniesByInvoices(
        toDateStart(nextFilters.dateFrom),
        toDateEnd(nextFilters.dateTo),
      );
      setInvoices(result);
      setAppliedFilters(nextFilters);
    } catch (error) {
      console.error('Error loading top companies by invoices', error);
      setInvoices([]);
      setErrors(await responseErrors(error as ResponseError));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData(appliedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spd.reports]);

  let invoicesTotal = 0;
  for (const row of invoices) {
    invoicesTotal += Number(row.totalIssuedGross || 0);
  }

  return (
    <>
      <TopCompaniesReportShell
        title="Największe firmy wg kwot faktur"
        totalText={numberFormatter(invoicesTotal, true)}
        totalClassName="text-success"
        loading={loading}
        filters={filters}
        setFilters={setFilters}
        onApply={() => {
          void loadData(filters);
        }}
        onClear={() => {
          const emptyFilters = { dateFrom: '', dateTo: '' };
          setFilters(emptyFilters);
          void loadData(emptyFilters);
        }}
        spinnerVariant="success"
      >
        <CompanyRankingList
          items={invoices}
          emptyText="Brak danych w wybranym zakresie."
          cardClassName="top-company-card top-company-card--invoices"
          badgeClassName="company-ranking-badge company-ranking-badge--invoices"
          badgeBg="success"
          getBadgeText={item =>
            numberFormatter(item.totalIssuedGross || '0', true)
          }
          getMetaText={item => {
            const count = item.invoiceCount || 0;
            return `${numberFormatter(count)} ${count === 1 ? 'faktura' : count < 5 ? 'faktury' : 'faktur'}`;
          }}
        />
        <ValidationAlert
          show={errors.length > 0}
          errors={errors}
          className="mt-3"
        />
      </TopCompaniesReportShell>
    </>
  );
};
